import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { FetchParams } from "../../../components/controls/table-view";
import { ApplicationError } from "../../../models/errors/application-error";
import {
	ApplicationReturn,
	ApplicationReturnList,
	ReturnStatus,
} from "../../../models/returns/applicant-return";
import ReturnsService from "../../../services/returns/returns-service";
import { RootState } from "../../store";

interface ReturnsState {
	returns: ApplicationReturnList | null;
	selectedReturn: ApplicationReturn | null;
}

const initialState: ReturnsState = {
	returns: null,
	selectedReturn: null,
};

export const fetchReturns = createAsyncThunk<
	ApplicationReturnList | null,
	{ fetchParams: FetchParams; userOnly?: boolean; status?: ReturnStatus; supplierId?: number; branchId?: number },
	{ state: RootState }
>("returns/get", async ({ fetchParams, userOnly = false, status, supplierId, branchId  }, { dispatch, getState, rejectWithValue }) => {
	const state = getState();
	const returnsService = new ReturnsService();

	try {
		var currentPosition: number = state.returns.returns?.currentPosition ?? 0;
		var fetchNext: number = 15;
		var count: number = state.returns.returns?.count ?? -1;

		if (fetchParams.reset) {
			currentPosition = 0;
			count = -1;
			dispatch(resetReturns());
		}

		if (currentPosition != count) {
			return await returnsService.get(
				currentPosition,
				fetchNext,
				false,
				fetchParams.searchText ?? "",
				userOnly,
				status,
                supplierId,
                branchId
			);
		}

		return null;
	} catch (error: any) {
		const apiError = ApplicationError.handleApiError(error, {});
		return rejectWithValue(apiError);
	}
});

export const fetchReturnById = createAsyncThunk<
	ApplicationReturn | null,
	number,
	{ state: RootState }
>("returns/getSingle", async (id, { rejectWithValue }) => {
	const returnsService = new ReturnsService();

	try {
		return await returnsService.getById(id);
	} catch (error: any) {
		const apiError = ApplicationError.handleApiError(error, {});
		return rejectWithValue(apiError);
	}
});

export const insertReturn = createAsyncThunk<
	ApplicationReturn | null,
	ApplicationReturn,
	{ state: RootState }
>("returns/insert", async (applicationReturn, { rejectWithValue }) => {
	const returnsService = new ReturnsService();

	try {
		return await returnsService.insert(applicationReturn);
	} catch (error: any) {
		const apiError = ApplicationError.handleApiError(error, {});
		return rejectWithValue(apiError);
	}
});

export const updateReturn = createAsyncThunk<
	ApplicationReturn | null,
	ApplicationReturn,
	{ state: RootState }
>("returns/update", async (applicationReturn, { rejectWithValue }) => {
	const returnsService = new ReturnsService();

	try {
		return await returnsService.update(applicationReturn.id, applicationReturn);
	} catch (error: any) {
		const apiError = ApplicationError.handleApiError(error, {});
		return rejectWithValue(apiError);
	}
});

export const deleteReturn = createAsyncThunk<
	number,
	number,
	{ state: RootState }
>("returns/delete", async (id, { rejectWithValue }) => {
	const returnsService = new ReturnsService();

	try {
		await returnsService.delete(id);
		return id;
	} catch (error: any) {
		const apiError = ApplicationError.handleApiError(error, {});
		return rejectWithValue(apiError);
	}
});

const returnsSlice = createSlice({
	name: "returns",
	initialState,
	reducers: {
		resetReturns: (state) => {
			state.returns = initialState.returns;
		},
		resetSelectedReturn: (state) => {
			state.selectedReturn = initialState.selectedReturn;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchReturns.fulfilled, (state, action: PayloadAction<ApplicationReturnList | null>) => {
				if (action.payload) {
					if (state.returns?.returns) {
						state.returns.returns = state.returns.returns.concat(action.payload.returns);
						state.returns.currentPosition = action.payload.currentPosition;
						state.returns.count = action.payload.count;
					} else {
						state.returns = action.payload;
					}
				}
			})
			.addCase(fetchReturnById.fulfilled, (state, action: PayloadAction<ApplicationReturn | null>) => {
				if (action.payload) {
					state.selectedReturn = action.payload;
				}
			})
			.addCase(insertReturn.fulfilled, (state, action: PayloadAction<ApplicationReturn | null>) => {
				if (action.payload && state.returns?.returns) {
					state.returns.returns.unshift(action.payload);
					state.returns.count += 1;
					state.returns.currentPosition += 1;
				}
			})
			.addCase(updateReturn.fulfilled, (state, action: PayloadAction<ApplicationReturn | null>) => {
				const updatedReturn = action.payload;
				if (updatedReturn && state.returns?.returns) {
					state.returns.returns = state.returns.returns.map((applicationReturn) => {
						if (applicationReturn.id !== updatedReturn.id) {
							return applicationReturn;
						}

						return {
							...applicationReturn,
							...updatedReturn,
							supplierId: updatedReturn.supplierId || applicationReturn.supplierId,
							supplierProductId: updatedReturn.supplierProductId || applicationReturn.supplierProductId,
							supplierName: updatedReturn.supplierName ?? applicationReturn.supplierName,
							productName: updatedReturn.productName ?? applicationReturn.productName,
							createdBy: updatedReturn.createdBy ?? applicationReturn.createdBy,
							notes: updatedReturn.notes ?? applicationReturn.notes,
							created:
								updatedReturn.createdBy == null && applicationReturn.created
									? applicationReturn.created
									: updatedReturn.created,
						};
					});
					if (state.selectedReturn?.id === updatedReturn.id && state.returns.returns) {
						state.selectedReturn =
							state.returns.returns.find((x) => x.id === updatedReturn.id) ?? state.selectedReturn;
					}
				}
			})
			.addCase(deleteReturn.fulfilled, (state, action: PayloadAction<number>) => {
				if (state.returns?.returns) {
					state.returns.returns = state.returns.returns.filter((x) => x.id != action.payload);
					state.returns.count -= 1;
					state.returns.currentPosition -= 1;
					if (state.selectedReturn?.id === action.payload) {
						state.selectedReturn = null;
					}
				}
			});
	},
});

export const { resetReturns, resetSelectedReturn } = returnsSlice.actions;

export default returnsSlice.reducer;
